import { remoteDbs, sandboxDbs, type DbEnv } from "../constants/db";
import type { Server, ServerEnvironment } from "../constants/env";
import { ENVIRONMENTS, isServer } from "../constants/env";
import Flags from "../constants/flags";

/**
 * This is for use in the client.
 * It will return the current environment based PUBLIC_* environment variables.
 * @returns ServerEnvironment
 */
export function getClientEnvironment(): ServerEnvironment {
  const isLocalhost =
    (!isServer && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")) ||
    (isServer && !remoteDbs.includes(Flags.DB_ENV));

  const cognitoEnv = Flags.COGNITO_ENV || ENVIRONMENTS.develop;
  const dbEnv = Flags.DB_ENV || ENVIRONMENTS.local;
  const gitTag = Flags.GIT_TAG || "N/A";

  return {
    server: isLocalhost ? ENVIRONMENTS.local : ENVIRONMENTS[dbEnv as Server],
    localhost: isLocalhost,
    db: dbEnv,
    cognito: cognitoEnv,
    branch: Flags.GIT_BRANCH,
    tag: gitTag,
    commit: Flags.GIT_COMMIT_HASH,
    developmentMode: Flags.IS_DEV_MODE,
  };
}

/**
 * This is for use on the server.
 * It will return the current server configuration based on process.env.* variables.
 * @returns ServerEnvironment
 */
export function getServerEnvironment(): ServerEnvironment {
  const dbEnv = (process.env.DB_ENV ?? ENVIRONMENTS.local) as DbEnv;
  const cognitoEnv = (process.env.COGNITO_ENV ?? ENVIRONMENTS.develop) as Server;
  const isLocal = !remoteDbs.includes(dbEnv);

  return {
    server: isLocal ? ENVIRONMENTS.local : ENVIRONMENTS[dbEnv as Server],
    localhost: !isServer,
    db: dbEnv,
    cognito: cognitoEnv,
    branch: process.env.GIT_BRANCH ?? "",
    tag: process.env.GIT_TAG ?? "",
    commit: process.env.GIT_COMMIT_HASH ?? "",
    developmentMode: process.env.NODE_ENV !== "production",
  };
}

/**
 * This is for shared tooling, when you aren't sure which env you're in.
 * It will check if you're on a server and get the correct env accordingly.
 * @returns ServerEnvironment
 */
export function getSharedEnvironment(): ServerEnvironment {
  if (isServer) {
    return getServerEnvironment();
  } else {
    return getClientEnvironment();
  }
}

/**
 * Whether Higharc staff may self-provision a DB user and self-promote to SUPER_ADMIN
 * on empty databases (sandbox / PR / local bootstrap).
 *
 * Gated on `DB_ENV`, not Cognito: sandbox, PR, and develop share develop's user pool
 * (`cognitoConfigForEnv`), so Cognito env alone cannot distinguish them. Develop, qa,
 * demos, stage, and prod require an invite or an existing admin.
 */
export function isHigharcStaffBootstrapEnv(dbEnv: DbEnv = getSharedEnvironment().db): boolean {
  return (
    dbEnv === "pr" ||
    dbEnv === "local" ||
    dbEnv === "local_test" ||
    dbEnv === "docker_test" ||
    sandboxDbs.some((sandbox) => sandbox === dbEnv)
  );
}
